export type ApiError = {
  status: number;
  code: string;
  detail: string;
  fields: Record<string, string[]>;
  requestId?: string;
};

function csrfToken() {
  const cookie = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("csrftoken="));
  return cookie ? decodeURIComponent(cookie.slice("csrftoken=".length)) : "";
}

function parseError(status: number, payload: unknown, requestId?: string): ApiError {
  const fields: Record<string, string[]> = {};
  let detail = "Something went wrong. Please try again.";
  let code = "request_failed";

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const envelope = record.error && typeof record.error === "object" ? record.error as Record<string, unknown> : record;
    if (typeof envelope.message === "string") detail = envelope.message;
    else if (typeof envelope.detail === "string") detail = envelope.detail;
    if (typeof envelope.code === "string") code = envelope.code;
    if (!requestId && typeof envelope.request_id === "string") requestId = envelope.request_id;
    const fieldSource = envelope.fields && typeof envelope.fields === "object" ? envelope.fields as Record<string, unknown> : envelope;
    for (const [key, value] of Object.entries(fieldSource)) {
      if (["detail", "message", "code", "request_id", "fields"].includes(key)) continue;
      if (typeof value === "string") fields[key] = [value];
      if (Array.isArray(value)) fields[key] = value.filter((item): item is string => typeof item === "string");
    }
  }

  return { status, code, detail, fields, requestId };
}

const inFlightReads = new Map<string, Promise<unknown>>();

async function performRequest<T>(path: string, options: RequestInit): Promise<T> {
  const headers = new Headers(options.headers);
  const method = (options.method ?? "GET").toUpperCase();
  if (options.body) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) headers.set("X-CSRFToken", csrfToken());

  const response = await fetch(path, { ...options, headers, credentials: options.credentials ?? "same-origin" });
  if (!response.ok) {
    let payload: unknown = null;
    try { payload = await response.json(); } catch { /* Never expose an HTML error response. */ }
    throw parseError(response.status, payload, response.headers.get("X-Request-ID") ?? undefined);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  if (method !== "GET" || options.signal) return performRequest<T>(path, options);

  const headerKey = Array.from(new Headers(options.headers).entries())
    .sort(([left], [right]) => left.localeCompare(right));
  const requestKey = JSON.stringify([path, headerKey]);
  const existing = inFlightReads.get(requestKey);
  if (existing) return existing as Promise<T>;

  const pending = performRequest<T>(path, options).finally(() => {
    if (inFlightReads.get(requestKey) === pending) inFlightReads.delete(requestKey);
  });
  inFlightReads.set(requestKey, pending);
  return pending;
}

export function apiErrorMessage(reason: unknown, fallback = "Something went wrong. Please try again.") {
  if (reason && typeof reason === "object") {
    if ("fields" in reason && reason.fields && typeof reason.fields === "object") {
      for (const messages of Object.values(reason.fields as Record<string, unknown>)) {
        if (Array.isArray(messages)) {
          const message = messages.find((item): item is string => typeof item === "string" && item.length > 0);
          if (message) return message;
        }
      }
    }
    if ("detail" in reason && typeof reason.detail === "string") return reason.detail;
  }
  return reason instanceof Error ? reason.message : fallback;
}
